import { useState, useMemo } from "react";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  PlusCircle,
  Globe,
  Trash2,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FilterToolbar } from "@/components/common/FilterToolbar";
import { LocationSelect, RegionSelect } from "@/components/common/entity-select";
import { cn } from "@/lib/utils";
import { getApiErrorFields, getApiErrorMessage } from "@/lib/api/api";
import { userApi, type UserRoleAssignment } from "@/lib/api/services/user.api";
import { roleApi } from "@/lib/api/services/role.api";
import { companyApi } from "@/lib/api/services/company.api";
import { useAuth } from "@/hooks/useAuth";
import type { Location } from "@/types/location";
import { UserPermissionsSection } from "./UserPermissionsSection";

type ScopeType = "ORGANIZATION" | "COMPANY" | "REGION" | "LOCATION";

// ── Helpers ───────────────────────────────────────────────────────────────────

function findGrantingRole(
  location: Location,
  roleAssignments: UserRoleAssignment[],
): UserRoleAssignment | undefined {
  return (
    roleAssignments.find(
      (r) => r.scopeType === "LOCATION" && r.scopeId === location.id,
    ) ??
    roleAssignments.find(
      (r) => r.scopeType === "REGION" && r.scopeId === location.regionId,
    ) ??
    roleAssignments.find(
      (r) => r.scopeType === "COMPANY" && r.scopeId === location.companyId,
    ) ??
    roleAssignments.find((r) => r.scopeType === "ORGANIZATION")
  );
}

function scopeLabel(r: UserRoleAssignment, i18n: I18n): string {
  if (!r.scopeType) return r.role.name;
  const type = r.scopeType.charAt(0) + r.scopeType.slice(1).toLowerCase();
  const roleName = r.role.name;
  return i18n._(msg`${roleName} · via ${type}`);
}

// ── Location card for access control context ──────────────────────────────────

function AccessLocationCard({
  location,
  grantingRole,
  onRemove,
}: {
  location: Location;
  grantingRole?: UserRoleAssignment;
  onRemove?: () => void;
}) {
  const { t, i18n } = useLingui();

  return (
    <div className="bg-(--color-surface-muted) border border-(--shell-border) flex gap-3 items-center px-3 py-4 rounded-lg">
      <div className="size-13 rounded-[10px] bg-(--color-surface-content) border border-(--shell-border) shrink-0 flex items-center justify-center overflow-hidden">
        <Globe className="size-5 text-(--color-text-secondary)" />
      </div>

      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
            {location.name}
          </span>
          <Badge
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium border-transparent",
              location.isActive
                ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                : "bg-(--color-surface-accent) text-(--color-text-secondary) hover:bg-(--color-surface-accent)",
            )}
          >
            {location.isActive ? t`Active` : t`Inactive`}
          </Badge>
        </div>
        <span className="text-sm text-(--color-text-secondary) truncate">
          {[location.code, location.region?.name, location.timezone]
            .filter(Boolean)
            .join(" • ")}
        </span>
        {grantingRole && (
          <span className="text-xs text-(--color-text-secondary) truncate">
            {scopeLabel(grantingRole, i18n)}
          </span>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="size-13 bg-(--color-surface-content) border border-(--shell-border) rounded-[10px] flex items-center justify-center shrink-0 hover:bg-(--color-surface-muted) transition-colors"
          >
            <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
            onSelect={onRemove}
            disabled={!onRemove}
          >
            <Trash2 className="size-4" />
            {t`Remove access`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ── Region group ──────────────────────────────────────────────────────────────

function UserRegionGroup({
  regionName,
  locations,
  roleAssignments,
  expanded,
  onToggle,
  onRemoveLocation,
}: {
  regionName: string;
  locations: Location[];
  roleAssignments: UserRoleAssignment[];
  expanded: boolean;
  onToggle: () => void;
  onRemoveLocation: (location: Location, grantingRole: UserRoleAssignment) => void;
}) {
  const { t } = useLingui();

  return (
    <div
      className={cn(
        "border border-(--color-border) rounded-[10px] overflow-hidden",
        expanded ? "bg-(--color-surface-content)" : "bg-(--color-surface-muted)",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-3 p-3",
          expanded && "border-b border-(--color-border) bg-(--color-surface-muted)",
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          className="size-12 bg-(--color-surface-content) border border-(--color-border) rounded-[10px] flex items-center justify-center shrink-0 hover:bg-(--color-surface-muted) transition-colors"
        >
          {expanded ? (
            <ChevronUp className="size-5 text-(--color-text-secondary)" />
          ) : (
            <ChevronDown className="size-5 text-(--color-text-secondary)" />
          )}
        </button>

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
            {regionName}
          </span>
          <div className="flex items-center gap-2 text-base leading-snug">
            <span className="text-(--color-text-secondary)">{t`Locations:`}</span>
            <span className="font-medium text-(--color-text-primary)">{locations.length}</span>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 flex flex-col gap-2">
          {locations.map((loc) => {
            const grant = findGrantingRole(loc, roleAssignments);
            return (
              <AccessLocationCard
                key={loc.id}
                location={loc}
                grantingRole={grant}
                onRemove={grant ? () => onRemoveLocation(loc, grant) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Add Access dialog ─────────────────────────────────────────────────────────

function AddAccessDialog({
  open,
  onOpenChange,
  userId,
  organization,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: number;
  /** The organization the user belongs to: the only organization a role can be scoped to for them. */
  organization?: UserOrganization | null;
  onSuccess: () => void;
}) {
  const { t } = useLingui();
  const { isAuthenticated } = useAuth();
  const [roleId, setRoleId] = useState("");
  const [scopeType, setScopeType] = useState<ScopeType | "">("");
  const [scopeId, setScopeId] = useState("");

  const { data: roles = [] } = useQuery({
    queryKey: ["roles"],
    queryFn: () => roleApi.getAll(),
    enabled: isAuthenticated && open,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companyApi.getAll(),
    enabled: isAuthenticated && open && scopeType === "COMPANY",
  });


  // An Organization scope has no picker: the API only accepts the user's own
  // organization (it rejects any other with a 403), so the id is filled in here
  // rather than sent as Number("") === 0.
  const organizationId = organization?.id ?? null;
  const resolvedScopeId =
    scopeType === "ORGANIZATION" ? (organizationId ?? 0) : Number(scopeId);

  const addMutation = useMutation({
    mutationFn: () =>
      userApi.addScope(String(userId), {
        roleId: Number(roleId),
        scopeType: scopeType as ScopeType,
        scopeId: resolvedScopeId,
      }),
    onSuccess: () => {
      toast.success(t`Access granted`);
      onSuccess();
      onOpenChange(false);
      setRoleId("");
      setScopeType("");
      setScopeId("");
    },
    onError: (err: unknown) => {
      // A field-level rejection carries the validator's own wording ("scopeId must
      // not be less than 1"), which names a property, not something to fix here.
      // Business-rule messages ("already granted", "does not belong to ...") are
      // plain sentences and are shown as sent.
      const message =
        Object.keys(getApiErrorFields(err)).length > 0
          ? t`Access could not be granted. Check the role and where it applies, then try again.`
          : getApiErrorMessage(err, t`Failed to grant access`);
      toast.error(message);
    },
  });

  const scopeTypeLabel: Record<ScopeType, string> = {
    ORGANIZATION: t`Organization`,
    COMPANY: t`Concept`,
    REGION: t`Region`,
    LOCATION: t`Location`,
  };

  const needsEntityPicker = scopeType && scopeType !== "ORGANIZATION";
  const canSubmit =
    !!roleId &&
    !!scopeType &&
    (scopeType === "ORGANIZATION" ? organizationId !== null : !!scopeId);

  const handleScopeTypeChange = (v: string) => {
    setScopeType(v as ScopeType);
    setScopeId("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-120">
        <DialogHeader>
          <DialogTitle>{t`Add access`}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Role picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-(--color-text-primary)">{t`Role`}</label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger aria-label={t`Select role`}>
                <SelectValue placeholder={t`Select a role…`} />
              </SelectTrigger>
              <SelectContent>
                {roles
                  .filter((r) => r.code !== "SUPER_ADMIN")
                  .map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope type picker */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-(--color-text-primary)">{t`Scope type`}</label>
            <Select value={scopeType} onValueChange={handleScopeTypeChange}>
              <SelectTrigger aria-label={t`Select scope type`}>
                <SelectValue placeholder={t`Select scope…`} />
              </SelectTrigger>
              <SelectContent>
                {(["ORGANIZATION", "COMPANY", "REGION", "LOCATION"] as ScopeType[]).map((st) => (
                  <SelectItem key={st} value={st}>
                    {scopeTypeLabel[st]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Organization scope: fixed to the user's organization, nothing to pick */}
          {scopeType === "ORGANIZATION" && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-(--color-text-primary)">
                {scopeTypeLabel.ORGANIZATION}
              </span>
              {organization ? (
                <p className="text-sm text-(--color-text-secondary)">
                  {organization.name}
                </p>
              ) : (
                <p role="alert" className="text-sm text-(--color-danger-text)">
                  {t`This user does not belong to an organization yet, so a role cannot be granted across one.`}
                </p>
              )}
            </div>
          )}

          {/* Scope entity picker */}
          {needsEntityPicker && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-(--color-text-primary)">
                {scopeTypeLabel[scopeType as ScopeType]}
              </label>
              {scopeType === "COMPANY" && (
                <Select value={scopeId} onValueChange={setScopeId}>
                  <SelectTrigger aria-label={scopeTypeLabel["COMPANY"]}>
                    <SelectValue placeholder={t`Select…`} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {scopeType === "REGION" && (
                <RegionSelect
                  value={scopeId ? Number(scopeId) : null}
                  onChange={(v) => setScopeId(String(v))}
                  placeholder={t`Select region…`}
                />
              )}
              {scopeType === "LOCATION" && (
                <LocationSelect
                  value={scopeId ? Number(scopeId) : null}
                  onChange={(v) => setScopeId(String(v))}
                  placeholder={t`Select location…`}
                />
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t`Cancel`}
          </Button>
          <Button
            className="bg-(--color-action) text-(--color-action-fg) hover:bg-(--color-action)/90"
            disabled={!canSubmit || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? t`Granting…` : t`Grant access`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Role assignments, grouped by scope ────────────────────────────────────────

const SCOPE_ORDER: ScopeType[] = ["ORGANIZATION", "COMPANY", "REGION", "LOCATION"];

/**
 * The list used to read "Company scope · ID 40", which tells a manager nothing.
 * Each scope gets a heading and a sentence saying how far the role actually
 * reaches, and rows name the concept/region/location wherever the page already
 * has that name to hand.
 */
function useScopeCopy(): Record<ScopeType, { title: string; description: string }> {
  const { t } = useLingui();
  return {
    ORGANIZATION: {
      title: t`Organization`,
      description: t`Applies everywhere in this organization — every concept, region and location.`,
    },
    COMPANY: {
      title: t`Concept`,
      description: t`Applies to every location that belongs to the named concept.`,
    },
    REGION: {
      title: t`Region`,
      description: t`Applies to every location in the named region.`,
    },
    LOCATION: {
      title: t`Location`,
      description: t`Applies to the named location only.`,
    },
  };
}

function scopeEntityName(
  assignment: UserRoleAssignment,
  locations: Location[],
  companyNames: Map<number, string>,
): string | undefined {
  if (assignment.scopeId == null) return undefined;
  switch (assignment.scopeType) {
    case "LOCATION":
      return locations.find((loc) => loc.id === assignment.scopeId)?.name;
    case "REGION":
      return locations.find((loc) => loc.regionId === assignment.scopeId)?.region?.name;
    case "COMPANY":
      return companyNames.get(assignment.scopeId);
    default:
      return undefined;
  }
}

function AssignmentRow({
  assignment,
  detail,
  canEdit,
  onRemove,
}: {
  assignment: UserRoleAssignment;
  detail: string;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const { t } = useLingui();

  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-(--shell-border) bg-(--color-surface-muted) px-3 py-3">
      <div className="size-10 shrink-0 rounded-[8px] bg-(--color-surface-content) border border-(--shell-border) flex items-center justify-center">
        <ShieldCheck className="size-4 text-(--color-text-secondary)" />
      </div>

      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="text-sm font-medium text-(--color-text-primary)">
          {assignment.role.name}
        </span>
        <span className="text-xs text-(--color-text-secondary) truncate">{detail}</span>
      </div>

      {canEdit && (
        <button
          type="button"
          aria-label={t`Remove ${assignment.role.name} access`}
          onClick={onRemove}
          className="size-10 shrink-0 bg-(--color-surface-content) border border-(--shell-border) rounded-[10px] flex items-center justify-center hover:bg-(--color-danger)/10 transition-colors"
        >
          <Trash2 className="size-4 text-(--color-danger-text)" />
        </button>
      )}
    </div>
  );
}

function ScopeAssignmentsSection({
  roleAssignments,
  locations,
  companyNames,
  canEdit,
  onRemove,
}: {
  roleAssignments: UserRoleAssignment[];
  locations: Location[];
  companyNames: Map<number, string>;
  canEdit: boolean;
  onRemove: (assignment: UserRoleAssignment, description: string) => void;
}) {
  const { t } = useLingui();
  const copy = useScopeCopy();

  // A role with no scope type is not scoped to anything — that is how a platform
  // role reaches every organization, so it gets its own bucket rather than being
  // filed under "Organization" and read as narrower than it is.
  const globalAssignments = roleAssignments.filter((assignment) => !assignment.scopeType);

  const groups = SCOPE_ORDER.map((scopeType) => ({
    scopeType,
    ...copy[scopeType],
    assignments: roleAssignments.filter((assignment) => assignment.scopeType === scopeType),
  })).filter((group) => group.assignments.length > 0);

  if (roleAssignments.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-lg font-medium text-(--color-text-primary)">{t`Roles and scope`}</p>
        <p className="text-sm text-(--color-text-secondary) py-2">
          {t`No roles assigned yet. Use “Add access” to give this user a role somewhere.`}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <p className="text-lg font-medium text-(--color-text-primary)">{t`Roles and scope`}</p>
        <p className="text-sm text-(--color-text-secondary)">
          {t`Each role is granted somewhere. The heading says how far that grant reaches.`}
        </p>
      </div>

      {globalAssignments.length > 0 && (
        <section className="flex flex-col gap-2" aria-label={t`Platform-wide`}>
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Platform-wide`}</p>
          <p className="text-xs text-(--color-text-secondary)">
            {t`Not tied to a scope — applies across every organization.`}
          </p>
          {globalAssignments.map((assignment) => (
            <AssignmentRow
              key={assignment.id}
              assignment={assignment}
              detail={t`Platform-wide`}
              canEdit={canEdit}
              onRemove={() => onRemove(assignment, t`platform-wide access`)}
            />
          ))}
        </section>
      )}

      {groups.map((group) => (
        <section className="flex flex-col gap-2" key={group.scopeType} aria-label={group.title}>
          <p className="text-sm font-medium text-(--color-text-primary)">{group.title}</p>
          <p className="text-xs text-(--color-text-secondary)">{group.description}</p>
          {group.assignments.map((assignment) => {
            const name = scopeEntityName(assignment, locations, companyNames);
            const detail =
              group.scopeType === "ORGANIZATION"
                ? t`Organization-wide`
                : (name ?? t`${group.title} #${assignment.scopeId}`);
            return (
              <AssignmentRow
                key={assignment.id}
                assignment={assignment}
                detail={detail}
                canEdit={canEdit}
                onRemove={() => onRemove(assignment, detail)}
              />
            );
          })}
        </section>
      ))}
    </div>
  );
}

// ── Confirm remove dialog ─────────────────────────────────────────────────────

function ConfirmRemoveDialog({
  open,
  onOpenChange,
  locationName,
  grantLabel,
  isPending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  locationName: string;
  grantLabel: string;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const { t } = useLingui();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-105">
        <DialogHeader>
          <DialogTitle>{t`Remove access`}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-(--color-text-secondary)">
          {t`This will remove the user's access to`}{" "}
          <span className="font-medium text-(--color-text-primary)">{locationName}</span>
          {". "}
          {t`The access was granted via`}{" "}
          <span className="font-medium text-(--color-text-primary)">{grantLabel}</span>
          {". "}
          {t`Other locations granted through the same scope will also lose access.`}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t`Cancel`}
          </Button>
          <Button
            variant="destructive"
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending ? t`Removing…` : t`Remove`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

type UserOrganization = { id: number; name: string };

interface UserAccessControlTabProps {
  userId: number;
  organization?: UserOrganization | null;
  locations: Location[];
  roleAssignments: UserRoleAssignment[];
  canEdit: boolean;
}

export function UserAccessControlTab({
  userId,
  organization,
  locations,
  roleAssignments,
  canEdit,
}: UserAccessControlTabProps) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [viewMode, setViewMode] = useState<"list" | "tile" | "panel">("list");
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    location: { id: number; name: string };
    grant: UserRoleAssignment;
  } | null>(null);

  const [expandedRegions, setExpandedRegions] = useState<Set<number | null>>(() => {
    const ids = new Set<number | null>();
    for (const loc of locations) ids.add(loc.regionId ?? null);
    return ids;
  });

  // Only fetched when a concept-scoped role is actually on screen — otherwise
  // the rows have nothing to name and the request would be wasted.
  const hasCompanyScope = roleAssignments.some((assignment) => assignment.scopeType === "COMPANY");
  const { data: scopeCompanies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companyApi.getAll(),
    enabled: isAuthenticated && hasCompanyScope,
  });
  const companyNames = useMemo(
    () => new Map(scopeCompanies.map((company) => [company.id, company.name])),
    [scopeCompanies],
  );

  const statusOptions = [
    { value: "all", label: t`All` },
    { value: "active", label: t`Active` },
    { value: "inactive", label: t`Inactive` },
  ];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return locations.filter((loc) => {
      const matchesSearch =
        !q ||
        loc.name.toLowerCase().includes(q) ||
        (loc.region?.name ?? "").toLowerCase().includes(q) ||
        loc.code.toLowerCase().includes(q);
      const matchesStatus =
        status === "all" ||
        (status === "active" ? loc.isActive : !loc.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [locations, search, status]);

  const regionGroups = useMemo(() => {
    const map = new Map<number | null, { name: string; locations: Location[] }>();
    for (const loc of filtered) {
      const key = loc.regionId ?? null;
      if (!map.has(key)) {
        map.set(key, { name: loc.region?.name ?? t`No region`, locations: [] });
      }
      map.get(key)!.locations.push(loc);
    }
    return Array.from(map.entries()).map(([regionId, group]) => ({ regionId, ...group }));
  }, [filtered, t]);

  const toggleRegion = (regionId: number | null) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["users", userId, "locations"] });
    void queryClient.invalidateQueries({ queryKey: ["users", userId, "roles"] });
  };

  const removeMutation = useMutation({
    mutationFn: (userRoleId: number) =>
      userApi.removeScope(String(userId), userRoleId),
    onSuccess: () => {
      toast.success(t`Access removed`);
      setRemoveTarget(null);
      invalidate();
    },
    onError: (err: { message?: string }) => {
      toast.error(err?.message ?? t`Failed to remove access`);
    },
  });

  return (
    <div className="px-4 flex flex-col gap-6">
      {/* Section header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-medium leading-7 text-(--color-text-primary)">{t`Access`}</h2>
          <p className="text-sm leading-5 text-(--color-text-secondary)">
            {t`Which roles this user holds, where those roles apply, and what they allow.`}
          </p>
        </div>
        {canEdit && (
          <Button
            className="h-10 shrink-0 gap-2 rounded-[10px] bg-(--color-action) text-(--color-action-fg) hover:bg-(--color-action)/90 px-4"
            onClick={() => setAddOpen(true)}
          >
            <PlusCircle className="size-4" />
            <span className="text-sm font-medium">{t`Add access`}</span>
          </Button>
        )}
      </div>

      <div className="h-px bg-(--shell-border)" />

      <ScopeAssignmentsSection
        roleAssignments={roleAssignments}
        locations={locations}
        companyNames={companyNames}
        canEdit={canEdit}
        onRemove={(assignment, description) =>
          setRemoveTarget({ location: { id: assignment.scopeId ?? 0, name: description }, grant: assignment })
        }
      />

      <div className="h-px bg-(--shell-border)" />

      <FilterToolbar
        searchQuery={search}
        onSearchChange={setSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        statusFilter={status}
        onStatusFilterChange={setStatus}
        statusOptions={statusOptions}
        showPanel={false}
      />

      {filtered.length === 0 ? (
        <div className="py-14 text-center text-sm text-(--color-text-secondary)">
          {t`No locations found.`}
        </div>
      ) : (
        <>
          {/* Regions section */}
          <div className="flex flex-col gap-3">
            <p className="text-lg font-medium text-(--color-text-primary)">{t`Regions`}</p>
            {regionGroups.map(({ regionId, name, locations: groupLocs }) => (
              <UserRegionGroup
                key={regionId ?? "none"}
                regionName={name}
                locations={groupLocs}
                roleAssignments={roleAssignments}
                expanded={expandedRegions.has(regionId)}
                onToggle={() => toggleRegion(regionId)}
                onRemoveLocation={(loc, grant) => setRemoveTarget({ location: loc, grant })}
              />
            ))}
          </div>

          {/* Locations section */}
          <div className="flex flex-col gap-3">
            <p className="text-lg font-medium text-(--color-text-primary)">{t`Locations`}</p>
            <div className="flex flex-col gap-2">
              {filtered.map((loc) => {
                const grant = findGrantingRole(loc, roleAssignments);
                return (
                  <AccessLocationCard
                    key={loc.id}
                    location={loc}
                    grantingRole={grant}
                    onRemove={grant ? () => setRemoveTarget({ location: loc, grant }) : undefined}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="h-px bg-(--shell-border)" />

      <UserPermissionsSection roleAssignments={roleAssignments} />

      {/* Add access dialog */}
      <AddAccessDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        userId={userId}
        organization={organization}
        onSuccess={invalidate}
      />

      {/* Confirm remove dialog */}
      {removeTarget && (
        <ConfirmRemoveDialog
          open={!!removeTarget}
          onOpenChange={(v) => { if (!v) setRemoveTarget(null); }}
          locationName={removeTarget.location.name}
          grantLabel={scopeLabel(removeTarget.grant, i18n)}
          isPending={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate(removeTarget.grant.id)}
        />
      )}
    </div>
  );
}
